// src/pages/admin/ActualiteManagement.tsx
import { slugify } from '../../utils/slugify'; 
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Search, Calendar, Clock, Eye, Image as ImageIcon, X, Check, AlertCircle, Filter, Grid, List, ChevronDown, ChevronUp, FileText, User, Tag, Save, ArrowLeft, Upload, Table, ChevronLeft, ChevronRight, RefreshCw, ExternalLink, Loader2, Star } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { RichTextEditor } from '../../components/RichTextEditor';
import { supabase } from '../../lib/supabase';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publish_date: string;
  read_time: string;
  category: string;
  image_url: string;
  featured: boolean;
  slug: string;
  images?: ArticleImage[];
}

interface ArticleImage {
  id: string;
  article_id: string;
  image_url: string;
  alt_text?: string;
  display_order: number;
}

const CATEGORIES = ['Actualité', 'Essai', 'Fiabilité', 'Passion', 'Réglementation', 'Technique'];
const ARTICLES_PER_PAGE = 6;

// Less aggressive sanitization function
const sanitizeContent = (content: string): string => {
  if (!content) return '';
  
  try {
    // Only check for basic HTML validity without stripping tags
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    
    // Check for parsing errors
    const parsererror = doc.querySelector('parsererror');
    if (parsererror) {
      console.error('HTML parsing error:', parsererror.textContent);
      // Return basic HTML if parsing fails
      return `<p>${content.replace(/<[^>]*>/g, '')}</p>`;
    }
    
    return content;
  } catch (e) {
    console.error('Error sanitizing content:', e);
    // Return basic HTML if error occurs
    return `<p>${content.replace(/<[^>]*>/g, '')}</p>`;
  }
};

export function ActualiteManagement() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'publish_date' | 'title' | 'category'>('publish_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dbInitialized, setDbInitialized] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const quillRef = useRef<ReactQuill>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  
  // Consolidated form state
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    publish_date: new Date().toISOString().split('T')[0],
    read_time: '5 min',
    category: CATEGORIES[0],
    image_url: '',
    featured: false,
    gallery_images: [] as string[]
  });

  useEffect(() => {
    checkDatabaseConnection();
  }, []);

  const fetchNewsItems = useCallback(async (page: number = 1, category: string = 'Toutes', search: string = '') => {
    try {
      setLoading(true);
      
      const from = (page - 1) * ARTICLES_PER_PAGE;
      const to = from + ARTICLES_PER_PAGE - 1;
      
      let query = supabase
        .from('actualite')
        .select('*', { count: 'exact' })
        .order('publish_date', { ascending: false })
        .range(from, to);
      
      if (category !== 'Toutes') {
        query = query.eq('category', category);
      }
      
      if (search) {
        query = query.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%`);
      }
      
      const { data: articles, error: fetchError, count } = await query;
      
      if (fetchError) throw fetchError;
      
      if (!articles || articles.length === 0) {
        setNewsItems([]);
        setTotalItems(count || 0);
        return;
      }
      
      const transformedData = articles.map((item: any) => ({
        ...item,
        content: item.content || '',
        images: []
      }));
      
      setNewsItems(transformedData);
      setTotalItems(count || 0);
      
    } catch (error) {
      handleDatabaseError(error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    if (dbInitialized) {
      fetchNewsItems(currentPage, selectedCategory, searchTerm);
    }
  }, [currentPage, selectedCategory, searchTerm, dbInitialized, fetchNewsItems]);

  const filteredItems = useMemo(() => {
    let filtered = newsItems;
    
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'publish_date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [newsItems, sortBy, sortOrder]);

  useEffect(() => {
    const pages = Math.ceil(totalItems / ARTICLES_PER_PAGE);
    setTotalPages(pages);
    
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [totalItems, currentPage]);

  const checkDatabaseConnection = async () => {
    try {
      const { data, error } = await supabase
        .from('actualite')
        .select('id')
        .limit(1);
      
      if (error) {
        if (error.code === 'PGRST205' || error.code === 'PGRST116') {
          setApiError(true);
          showNotification('error', 'La table "actualite" n\'est pas accessible via l\'API. Veuillez exécuter les commandes SQL ci-dessous dans l\'éditeur SQL de Supabase.');
        } else {
          showNotification('error', 'Erreur de connexion à la base de données: ' + error.message);
        }
      } else {
        setDbInitialized(true);
        fetchNewsItems();
      }
    } catch (error) {
      console.error('Error checking database connection:', error);
      setApiError(true);
      showNotification('error', 'Erreur lors de la vérification de la connexion à la base de données');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleDatabaseError = (error: any) => {
    console.error('Database error:', error);
    
    if (error.code === '57014') {
      showNotification('error', 'La requête a pris trop de temps. Veuillez réessayer avec des filtres plus spécifiques.');
    } else if (error.code === 'PGRST205' || error.code === 'PGRST116') {
      setApiError(true);
      showNotification('error', 'La table "actualite" n\'est pas accessible via l\'API. Veuillez exécuter les commandes SQL ci-dessous dans l\'éditeur SQL de Supabase.');
    } else {
      showNotification('error', 'Erreur de base de données: ' + error.message);
    }
  };

  const handleImagesUpload = async (files: File[]) => {
    if (!files || files.length === 0) return [];
    
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        const filePath = `actualites/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('images')
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        const { data } = supabase.storage
          .from('images')
          .getPublicUrl(filePath);
        
        uploadedUrls.push(data.publicUrl);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error('Erreur lors du téléchargement des images:', error);
      showNotification('error', 'Échec du téléchargement des images');
      return [];
    }
  };

  const handleNewArticle = () => {
    if (apiError) {
      showNotification('error', 'Impossible de créer un article. La base de données n\'est pas accessible.');
      return;
    }
    
    setEditingItemId(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      category: CATEGORIES[0],
      publish_date: new Date().toISOString().split('T')[0],
      read_time: '5 min',
      featured: false,
      image_url: '',
      gallery_images: []
    });
    setShowEditor(true);
  };

  const handleEdit = async (item: NewsItem) => {
    if (apiError) {
      showNotification('error', 'Impossible de modifier cet article. La base de données n\'est pas accessible.');
      return;
    }
    
    setEditingItemId(item.id);
    
    let galleryImages: string[] = [];
    try {
      const { data: images } = await supabase
        .from('article_images')
        .select('*')
        .eq('article_id', item.id)
        .order('display_order', { ascending: true });
      
      if (images) {
        galleryImages = images.map(img => img.image_url);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
    
    setFormData({
      title: item.title,
      excerpt: item.excerpt,
      content: item.content,
      author: item.author,
      category: item.category,
      publish_date: new Date(item.publish_date).toISOString().split('T')[0],
      read_time: item.read_time,
      featured: item.featured,
      image_url: item.image_url,
      gallery_images: galleryImages
    });
    setShowEditor(true);
  };

  const handleSaveArticle = async () => {
    if (apiError) {
      showNotification('error', 'Impossible d\'enregistrer cet article. La base de données n\'est pas accessible.');
      return;
    }
    
    if (!formData.title || !formData.excerpt || !formData.content || !formData.author || !formData.category || !formData.publish_date) {
      showNotification('error', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    
    try {
      // Use the less aggressive sanitization
      const sanitizedContent = sanitizeContent(formData.content);
      const sanitizedExcerpt = sanitizeContent(formData.excerpt);
      
      const articleSlug = slugify(formData.title);
      
      let articleId;
      
      if (editingItemId) {
        const { error } = await supabase
          .from('actualite')
          .update({
            title: formData.title,
            slug: articleSlug, 
            excerpt: sanitizedExcerpt,
            content: sanitizedContent,
            author: formData.author,
            category: formData.category,
            publish_date: new Date(formData.publish_date).toISOString(),
            read_time: formData.read_time,
            featured: formData.featured,
            image_url: formData.image_url
          })
          .eq('id', editingItemId);
        
        if (error) throw error;
        articleId = editingItemId;
        showNotification('success', 'Article mis à jour avec succès');
      } else {
        const { data, error } = await supabase
          .from('actualite')
          .insert({
            title: formData.title,
            slug: articleSlug, 
            excerpt: sanitizedExcerpt,
            content: sanitizedContent,
            author: formData.author,
            category: formData.category,
            publish_date: new Date(formData.publish_date).toISOString(),
            read_time: formData.read_time,
            featured: formData.featured,
            image_url: formData.image_url
          })
          .select();
        
        if (error) throw error;
        articleId = data[0].id;
        showNotification('success', 'Article créé avec succès');
      }
      
      if (formData.gallery_images.length > 0) {
        if (editingItemId) {
          try {
            await supabase
              .from('article_images')
              .delete()
              .eq('article_id', editingItemId);
          } catch (error) {
            console.warn('Error deleting existing images:', error);
          }
        }
        
        for (let i = 0; i < formData.gallery_images.length; i++) {
          try {
            await supabase
              .from('article_images')
              .insert({
                article_id: articleId,
                image_url: formData.gallery_images[i],
                display_order: i
              });
          } catch (error) {
            console.warn('Error inserting image:', error);
          }
        }
      }
      
      setShowEditor(false);
      fetchNewsItems(currentPage, selectedCategory, searchTerm);
    } catch (error) {
      handleDatabaseError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (apiError) {
      showNotification('error', 'Impossible de supprimer cet article. La base de données n\'est pas accessible.');
      return;
    }
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    
    try {
      try {
        await supabase
          .from('article_images')
          .delete()
          .eq('article_id', id);
      } catch (error) {
        console.warn('Error deleting article images:', error);
      }
      
      const { error } = await supabase
        .from('actualite')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      showNotification('success', 'Article supprimé avec succès');
      fetchNewsItems(currentPage, selectedCategory, searchTerm);
    } catch (error) {
      handleDatabaseError(error);
    }
  };

  const handleToggleFeatured = async (id: string, currentFeatured: boolean) => {
    try {
      const { error } = await supabase
        .from('actualite')
        .update({ featured: !currentFeatured })
        .eq('id', id);
      
      if (error) throw error;
      
      setNewsItems(prev => 
        prev.map(item => 
          item.id === id ? { ...item, featured: !currentFeatured } : item
        )
      );
      
      showNotification('success', `Article ${!currentFeatured ? 'ajouté aux' : 'retiré des'} vedettes avec succès`);
    } catch (error) {
      console.error('Error updating featured status:', error);
      showNotification('error', 'Erreur lors de la mise à jour du statut vedette');
    }
  };

  const handlePreviewArticle = () => {
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(`
        <html>
          <head>
            <title>Aperçu de l'article</title>
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
              body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
              .custom-table { width: 100%; border-collapse: collapse; margin: 1em 0; }
              .custom-table th, .custom-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .custom-table th { background-color: #f5f5f5; }
              .custom-table.bordered { border: 1px solid #e5e7eb; }
              .custom-table.bordered th, .custom-table.bordered td { border: 1px solid #e5e7eb; }
              .custom-table.striped tbody tr:nth-child(even) { background-color: #f9fafb; }
              @media (max-width: 640px) { .custom-table { font-size: 14px; } }
            </style>
          </head>
          <body>
            <h1 class="text-3xl font-bold mb-4">${formData.title}</h1>
            <p class="text-gray-600 mb-4 italic">${formData.excerpt}</p>
            ${formData.image_url ? `<img src="${formData.image_url}" alt="${formData.title}" class="w-full h-64 object-cover mb-4 rounded-lg">` : ''}
            ${formData.gallery_images.length > 0 ? `
              <h2 class="text-xl font-bold mt-8 mb-4">Galerie d'images</h2>
              <div class="grid grid-cols-3 gap-2">
                ${formData.gallery_images.map(img => `<img src="${img}" alt="Gallery image" class="w-full h-32 object-cover rounded">`).join('')}
              </div>
            ` : ''}
            <div class="prose">${formData.content}</div>
          </body>
        </html>
      `);
      previewWindow.document.close();
    }
  };

  const toggleExpandItem = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const retryConnection = () => {
    setApiError(false);
    setLoading(true);
    setRetryCount(prev => prev + 1);
    checkDatabaseConnection();
  };

  const resetSupabaseClient = () => {
    supabase.auth.refreshSession();
    retryConnection();
  };

  const openSupabaseDashboard = () => {
    window.open('https://app.supabase.com/project/kccqvrpgvgfnakuxhelh/sql', '_blank');
  };

  const getCurrentPageItems = () => {
    return filteredItems;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const LazyImage = ({ src, alt, className, ...props }: any) => {
    const [imageSrc, setImageSrc] = useState('');
    const [imageLoaded, setImageLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    
    useEffect(() => {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            setImageSrc(src);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      
      if (imgRef.current) {
        observer.observe(imgRef.current);
      }
      
      return () => observer.disconnect();
    }, [src]);
    
    return (
      <div ref={imgRef} className={`relative overflow-hidden ${className}`}>
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        )}
        <img
          src={imageSrc}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.currentTarget.src = '/placeholder-image.jpg';
            setImageLoaded(true);
          }}
          className={`transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
          {...props}
        />
      </div>
    );
  };

  return (
    <AdminLayout currentPage="actualite">
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
          {/* En-tête avec design moderne */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-3 sm:mb-0">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Gestion des Articles</h1>
                <p className="text-gray-600 text-sm">
                  Créez, modifiez et gérez vos articles d'actualité automobile
                </p>
                <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                  <span className="flex items-center">
                    <FileText className="h-3 w-3 mr-1" />
                    {totalItems} articles
                  </span>
                  <span className="flex items-center">
                    <Eye className="h-3 w-3 mr-1" />
                    {filteredItems.length} affichés
                  </span>
                  {totalPages > 1 && (
                    <span className="flex items-center">
                      <ChevronLeft className="h-3 w-3 mr-1" />
                      Page {currentPage} sur {totalPages}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleNewArticle}
                disabled={apiError}
                className={`inline-flex items-center px-4 py-2 font-medium rounded-lg shadow-md transform hover:scale-105 transition-all duration-200 text-sm ${
                  apiError 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white hover:shadow-lg'
                }`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvel Article
              </button>
            </div>
          </div>

          {/* API Error Message */}
          {apiError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Erreur de connexion à la base de données
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      La table "actualite" n'est pas accessible via l'API Supabase. 
                      Veuillez exécuter les commandes SQL suivantes dans l'éditeur SQL de Supabase:
                    </p>
                    <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono">
                      -- Vérifier si les tables existent<br />
                      SELECT table_name, table_schema <br />
                      FROM information_schema.tables <br />
                      WHERE table_name IN ('actualite', 'article_images') <br />
                      AND table_schema = 'public';<br /><br />
                      -- Vérifier les publications existantes<br />
                      SELECT * FROM pg_publication;<br /><br />
                      -- Créer une nouvelle publication si nécessaire<br />
                      CREATE PUBLICATION api_publication;<br />
                      ALTER PUBLICATION api_publication ADD TABLE public.actualite;<br />
                      ALTER PUBLICATION api_publication ADD TABLE public.article_images;<br />
                      NOTIFY pgrst, 'reload schema';
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <button
                      type="button"
                      onClick={retryConnection}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Réessayer ({retryCount})
                    </button>
                    <button
                      type="button"
                      onClick={resetSupabaseClient}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Réinitialiser la connexion
                    </button>
                    <button
                      type="button"
                      onClick={openSupabaseDashboard}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Ouvrir Supabase
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Barre de recherche et filtres améliorée */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col lg:flex-row gap-3">
              {/* Barre de recherche */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Rechercher par titre, contenu ou auteur..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    disabled={apiError}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent transition-all text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
              </div>
              
              {/* Filtres par catégorie */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setSelectedCategory('Toutes');
                    setCurrentPage(1);
                  }}
                  disabled={apiError}
                  className={`px-3 py-1 rounded-lg font-medium transition-all duration-200 text-sm ${
                    selectedCategory === 'Toutes'
                      ? 'bg-[#D4452C] text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } disabled:bg-gray-100 disabled:text-gray-500`}
                >
                  Toutes ({totalItems})
                </button>
                {CATEGORIES.map(category => {
                  const count = newsItems.filter(item => item.category === category).length;
                  return (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCurrentPage(1);
                      }}
                      disabled={apiError}
                      className={`px-3 py-1 rounded-lg font-medium transition-all duration-200 text-sm ${
                        selectedCategory === category
                          ? 'bg-[#D4452C] text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } disabled:bg-gray-100 disabled:text-gray-500`}
                    >
                      {category} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Options de tri et d'affichage */}
            <div className="flex flex-wrap items-center justify-between mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <span className="text-xs font-medium text-gray-700">Trier par :</span>
                <div className="flex items-center space-x-1">
                  {[
                    { key: 'publish_date', label: 'Date' },
                    { key: 'title', label: 'Titre' },
                    { key: 'category', label: 'Catégorie' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => {
                        if (sortBy === key) {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy(key as any);
                          setSortOrder(key === 'publish_date' ? 'desc' : 'asc');
                        }
                        setCurrentPage(1);
                      }}
                      disabled={apiError}
                      className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        sortBy === key 
                          ? 'bg-[#D4452C] text-white' 
                          : 'text-gray-700 hover:bg-gray-100'
                      } disabled:bg-gray-100 disabled:text-gray-500`}
                    >
                      <span>{label}</span>
                      {sortBy === key && (
                        sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mt-2 lg:mt-0">
                <span className="text-xs text-gray-600">Vue :</span>
                <button
                  onClick={() => setViewMode('grid')}
                  disabled={apiError}
                  className={`p-1 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-[#D4452C] text-white' 
                      : 'text-gray-500 hover:bg-gray-100'
                  } disabled:bg-gray-100 disabled:text-gray-500`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  disabled={apiError}
                  className={`p-1 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-[#D4452C] text-white' 
                      : 'text-gray-500 hover:bg-gray-100'
                  } disabled:bg-gray-100 disabled:text-gray-500`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Liste/Grille des articles */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="inline-block animate-spin h-10 w-10 text-[#D4452C] mb-3" />
                  <p className="mt-3 text-gray-600 font-medium text-sm">Chargement des Articles...</p>
                </div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-1">Aucun article trouvé</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {apiError 
                    ? 'La base de données n\'est pas accessible. Veuillez résoudre l\'erreur ci-dessus.'
                    : (searchTerm || selectedCategory !== 'Toutes' 
                      ? 'Aucun article ne correspond à vos critères de recherche.'
                      : 'Commencez par créer votre premier article d\'actualité.')
                  }
                </p>
                {(!searchTerm && selectedCategory === 'Toutes' && !apiError) && (
                  <button
                    onClick={handleNewArticle}
                    className="inline-flex items-center px-3 py-2 bg-[#D4452C] text-white rounded-lg hover:bg-[#b83a24] transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un article
                  </button>
                )}
              </div>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  // Vue en grille
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                    {getCurrentPageItems().map((item) => (
                      <div key={item.id} className="group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:border-[#D4452C] transition-all duration-200">
                        <div className="relative h-40 overflow-hidden">
                          <LazyImage 
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" 
                            src={item.image_url || '/placeholder-image.jpg'} 
                            alt={item.title}
                          />
                          {item.featured && (
                            <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                              À la une
                            </div>
                          )}
                          {item.images && item.images.length > 0 && (
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center">
                              <ImageIcon className="h-3 w-3 mr-1" />
                              {item.images.length}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {item.category}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {item.read_time}
                            </span>
                          </div>
                          <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#D4452C] transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">{truncateText(item.excerpt, 80)}</p>
                          <div className="flex items-center text-xs text-gray-500 mb-3">
                            <User className="h-3 w-3 mr-1" />
                            <span className="mr-2">{item.author}</span>
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>{new Date(item.publish_date).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => window.open(`/actualite/${item.slug || item.id}`, '_blank')}
                                className="p-1 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="Voir sur le site"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-1 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                title="Modifier"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleToggleFeatured(item.id, item.featured || false)}
                                className={`p-1 rounded-lg transition-colors ${
                                  item.featured
                                    ? 'text-yellow-600 hover:bg-yellow-50'
                                    : 'text-gray-400 hover:bg-gray-100'
                                }`}
                                title={item.featured ? 'Retirer des vedettes' : 'Ajouter aux vedettes'}
                              >
                                <Star className={`h-3 w-3 ${item.featured ? 'fill-current' : ''}`} />
                              </button>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              new Date(item.publish_date) > new Date() 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {new Date(item.publish_date) > new Date() ? 'Programmé' : 'Publié'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Vue en liste
                  <div className="divide-y divide-gray-200">
                    {getCurrentPageItems().map((item) => (
                      <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <LazyImage 
                              className="h-16 w-16 rounded-lg object-cover border border-gray-200" 
                              src={item.image_url || '/placeholder-image.jpg'} 
                              alt={item.title}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="text-base font-bold text-gray-900 hover:text-[#D4452C] transition-colors cursor-pointer">
                                    {item.title}
                                  </h3>
                                  {item.featured && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                      À la une
                                    </span>
                                  )}
                                  {item.images && item.images.length > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                      <ImageIcon className="h-3 w-3 mr-1" />
                                      {item.images.length}
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    new Date(item.publish_date) > new Date() 
                                      ? 'bg-orange-100 text-orange-800' 
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {new Date(item.publish_date) > new Date() ? 'Programmé' : 'Publié'}
                                  </span>
                                </div>
                                <div className="flex items-center text-xs text-gray-500 mb-2 space-x-3">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                                    {item.category}
                                  </span>
                                  <span className="flex items-center">
                                    <Calendar className="h-3 w-3 mr-1" />
                                    {new Date(item.publish_date).toLocaleDateString('fr-FR')}
                                  </span>
                                  <span className="flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {item.read_time}
                                  </span>
                                  <span className="flex items-center">
                                    <User className="h-3 w-3 mr-1" />
                                    {item.author}
                                  </span>
                                </div>
                                <p className="text-gray-600 line-clamp-1 mb-2 text-sm">{truncateText(item.excerpt, 100)}</p>
                                {expandedItems.has(item.id) && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{item.content}</p>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end space-y-2 ml-4">
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => window.open(`/actualite/${item.slug || item.id}`, '_blank')}
                                    className="p-1 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Voir sur le site"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="p-1 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                    title="Modifier"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(item.id)}
                                    className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleToggleFeatured(item.id, item.featured || false)}
                                    className={`p-1 rounded-lg transition-colors ${
                                      item.featured
                                        ? 'text-yellow-600 hover:bg-yellow-50'
                                        : 'text-gray-400 hover:bg-gray-100'
                                    }`}
                                    title={item.featured ? 'Retirer des vedettes' : 'Ajouter aux vedettes'}
                                  >
                                    <Star className={`h-4 w-4 ${item.featured ? 'fill-current' : ''}`} />
                                  </button>
                                </div>
                                <button
                                  onClick={() => toggleExpandItem(item.id)}
                                  className="text-xs text-[#D4452C] hover:text-[#b83a24] flex items-center transition-colors"
                                >
                                  {expandedItems.has(item.id) ? (
                                    <>
                                      <ChevronUp className="h-3 w-3 mr-1" />
                                      Réduire
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3 w-3 mr-1" />
                                      Voir plus
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Affichage de <span className="font-medium">{(currentPage - 1) * ARTICLES_PER_PAGE + 1}</span> à{' '}
                          <span className="font-medium">
                            {Math.min(currentPage * ARTICLES_PER_PAGE, totalItems)}
                          </span>{' '}
                          sur <span className="font-medium">{totalItems}</span> articles
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Précédent</span>
                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                          </button>
                          
                          {/* Page numbers */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 || 
                              page === totalPages || 
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    currentPage === page
                                      ? 'z-10 bg-[#D4452C] border-[#D4452C] text-white'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            } else if (
                              (page === 2 && currentPage > 3) || 
                              (page === totalPages - 1 && currentPage < totalPages - 2)
                            ) {
                              return (
                                <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                          
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Suivant</span>
                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rich Text Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {/* Main modal container with flex layout */}
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            
            {/* Header - fixed at the top */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingItemId ? 'Modifier l\'article' : 'Créer un nouvel article'}
              </h2>
              <button
                onClick={() => setShowEditor(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-hidden relative">
              <div className="h-full overflow-y-auto p-6 space-y-6">
                {/* Basic Information */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de base</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                        Titre de l'article
                      </label>
                      <input
                        type="text"
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                        placeholder="Titre de l'article"
                      />
                    </div>
                    <div>
                      <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                        Auteur
                      </label>
                      <input
                        type="text"
                        id="author"
                        value={formData.author}
                        onChange={(e) => setFormData({...formData, author: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                        placeholder="Nom de l'auteur"
                      />
                    </div>
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                        Catégorie
                      </label>
                      <select
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                      >
                        {CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="publishDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Date de publication
                      </label>
                      <input
                        type="date"
                        id="publishDate"
                        value={formData.publish_date}
                        onChange={(e) => setFormData({...formData, publish_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="readTime" className="block text-sm font-medium text-gray-700 mb-1">
                        Temps de lecture
                      </label>
                      <input
                        type="text"
                        id="readTime"
                        value={formData.read_time}
                        onChange={(e) => setFormData({...formData, read_time: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                        placeholder="ex: 5 min"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={formData.featured}
                        onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                        className="h-4 w-4 text-[#D4452C] focus:ring-[#D4452C] border-gray-300 rounded"
                      />
                      <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                        Mettre en avant
                      </label>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                      Extrait
                    </label>
                    <textarea
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
                      placeholder="Brève description de l'article"
                    />
                  </div>
                </div>

                {/* Top Image */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Image principale</h3>
                  <div className="space-y-4">
                    {formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Top image"
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => setFormData({...formData, image_url: ''})}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                          title="Supprimer l'image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                          Cliquez pour télécharger une image principale
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF jusqu'à 10MB
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            if (!e.target.files || e.target.files.length === 0) return;
                            
                            const file = e.target.files[0];
                            const fileExt = file.name.split('.').pop();
                            const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
                            const filePath = `actualites/${fileName}`;
                            
                            try {
                              const { error: uploadError } = await supabase.storage
                                .from('images')
                                .upload(filePath, file);
                              
                              if (uploadError) throw uploadError;
                              
                              const { data } = supabase.storage
                                .from('images')
                                .getPublicUrl(filePath);
                              
                              setFormData({...formData, image_url: data.publicUrl});
                            } catch (error) {
                              console.error('Error uploading top image:', error);
                              alert('Erreur lors du téléchargement de l\'image principale');
                            }
                          }}
                          className="hidden"
                          id="top-image-upload"
                        />
                        <label
                          htmlFor="top-image-upload"
                          className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#D4452C] hover:bg-[#b83a24] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] cursor-pointer"
                        >
                          Télécharger une image
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Gallery Images */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Galerie d'images</h3>
                  <div className="space-y-4">
                    {formData.gallery_images.length > 0 ? (
                      <div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          {formData.gallery_images.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image}
                                alt={`Gallery image ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg"
                              />
                              <button
                                onClick={() => {
                                  const newImages = [...formData.gallery_images];
                                  newImages.splice(index, 1);
                                  setFormData({...formData, gallery_images: newImages});
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Supprimer l'image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={async (e) => {
                            if (!e.target.files || e.target.files.length === 0) return;
                            
                            const files = Array.from(e.target.files);
                            const newImageUrls = [...formData.gallery_images];
                            
                            try {
                              for (const file of files) {
                                const fileExt = file.name.split('.').pop();
                                const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
                                const filePath = `actualites/${fileName}`;
                                
                                const { error: uploadError } = await supabase.storage
                                  .from('images')
                                  .upload(filePath, file);
                                
                                if (uploadError) throw uploadError;
                                
                                const { data } = supabase.storage
                                  .from('images')
                                  .getPublicUrl(filePath);
                                
                                newImageUrls.push(data.publicUrl);
                              }
                                
                              setFormData({...formData, gallery_images: newImageUrls});
                            } catch (error) {
                              console.error('Error uploading gallery images:', error);
                              alert('Erreur lors du téléchargement des images de la galerie');
                            }
                          }}
                          className="hidden"
                          id="gallery-images-upload"
                        />
                        <label
                          htmlFor="gallery-images-upload"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#D4452C] hover:bg-[#b83a24] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] cursor-pointer"
                        >
                          Ajouter plus d'images
                        </label>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                          Cliquez pour télécharger des images pour la galerie
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF jusqu'à 10MB
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={async (e) => {
                            if (!e.target.files || e.target.files.length === 0) return;
                            
                            const files = Array.from(e.target.files);
                            const newImageUrls = [...formData.gallery_images];
                            
                            try {
                              for (const file of files) {
                                const fileExt = file.name.split('.').pop();
                                const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
                                const filePath = `actualites/${fileName}`;
                                
                                const { error: uploadError } = await supabase.storage
                                  .from('images')
                                  .upload(filePath, file);
                                
                                if (uploadError) throw uploadError;
                                
                                const { data } = supabase.storage
                                  .from('images')
                                  .getPublicUrl(filePath);
                                
                                newImageUrls.push(data.publicUrl);
                              }
                                
                              setFormData({...formData, gallery_images: newImageUrls});
                            } catch (error) {
                              console.error('Error uploading gallery images:', error);
                              alert('Erreur lors du téléchargement des images de la galerie');
                            }
                          }}
                          className="hidden"
                          id="gallery-images-upload"
                        />
                        <label
                          htmlFor="gallery-images-upload"
                          className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#D4452C] hover:bg-[#b83a24] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] cursor-pointer"
                        >
                          Télécharger des images
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contenu de l'article</h3>
                  <div style={{ height: '400px' }}>
                    <RichTextEditor
                      ref={quillRef}
                      value={formData.content}
                      onChange={(content) => setFormData({...formData, content})}
                      placeholder="Rédigez le contenu de votre article ici..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - fixed at the bottom */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditor(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handlePreviewArticle}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
                >
                  Aperçu
                </button>
                <button
                  onClick={handleSaveArticle}
                  disabled={submitting || apiError}
                  className="px-4 py-2 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-none flex items-center text-sm"
                >
                  {submitting ? (
                    <>
                      <div className="mr-2 inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingItemId ? 'Mettre à jour' : 'Créer l\'article'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-xl z-50 max-w-sm border-l-4 ${
          notification.type === 'success' 
            ? 'bg-green-50 text-green-800 border-green-500' 
            : 'bg-red-50 text-red-800 border-red-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div className="ml-3">
              <p className="font-medium text-sm">{notification.message}</p>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}